(() => {
  const shared = window.PawuniThemeHotfixShared;
  if (!shared) return;

  const {
    JDGM_PREVIEW_BADGE_SELECTOR,
    applySyntheticJudgeMePreviewCounts,
    bindJudgeMeAndTeeinblueEvents,
    createRafThrottled,
    hasFacetedFilterUi,
    isCollectionOrSearchContext,
    mutationsContainAddedNode,
    runWithDelays,
  } = shared;

  // This split file also runs on index/page templates because product cards reuse collection markup there.
  const PRODUCT_CARD_REVIEW_CONTEXT_SELECTOR = [
    `.product-list ${JDGM_PREVIEW_BADGE_SELECTOR}`,
    `.slider-gallery ${JDGM_PREVIEW_BADGE_SELECTOR}`,
    `.rv-main ${JDGM_PREVIEW_BADGE_SELECTOR}`,
  ].join(', ');

  const hasProductCardReviewContext = () => Boolean(document.querySelector(PRODUCT_CARD_REVIEW_CONTEXT_SELECTOR));
  const COLLECTION_VIEW_STORAGE_KEY_PREFIX = 'pawuniCollectionView:';
  const COLLECTION_VIEW_VALUES = new Set(['grid', 'compact']);

  const clearGlobalInteractionLocks = () => {
    if (!hasFacetedFilterUi() || !isCollectionOrSearchContext()) return;

    if (typeof document.onkeydown === 'function') {
      document.onkeydown = null;
    }
    if (typeof document.oncontextmenu === 'function') {
      document.oncontextmenu = null;
    }
  };

  const syncFacetedSortInputs = () => {
    const sortValue = new URLSearchParams(window.location.search).get('sort_by');
    if (!sortValue) return;

    const forms = document.querySelectorAll('form[data-faceted-filter-form]');
    forms.forEach((form) => {
      let input = form.querySelector('input[name="sort_by"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'sort_by';
        form.appendChild(input);
      }
      input.value = sortValue;
    });
  };

  const getCollectionViewStorageKey = () => {
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    if (pathname.indexOf('/collections/') === 0) {
      return `${COLLECTION_VIEW_STORAGE_KEY_PREFIX}${pathname}`;
    }
    if (pathname.indexOf('/search') === 0 || document.body.classList.contains('template-search')) {
      return `${COLLECTION_VIEW_STORAGE_KEY_PREFIX}/search`;
    }
    return `${COLLECTION_VIEW_STORAGE_KEY_PREFIX}${pathname}`;
  };

  const getDefaultCollectionView = () => {
    const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
    return pathname.indexOf('/collections/') === 0 ? 'compact' : 'grid';
  };

  const getSavedCollectionView = () => {
    try {
      const value = window.localStorage && window.localStorage.getItem(getCollectionViewStorageKey());
      return COLLECTION_VIEW_VALUES.has(value) ? value : getDefaultCollectionView();
    } catch (error) {
      return getDefaultCollectionView();
    }
  };

  const setCollectionView = (view, { persist = false } = {}) => {
    if (!isCollectionOrSearchContext() || !COLLECTION_VIEW_VALUES.has(view)) return;

    document.body.setAttribute('data-pawuni-collection-view', view);
    document.querySelectorAll('.pawuni-collection-view-toggle__button[data-pawuni-collection-view]').forEach((button) => {
      const isActive = button.dataset.pawuniCollectionView === view;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    if (!persist) return;
    try {
      window.localStorage && window.localStorage.setItem(getCollectionViewStorageKey(), view);
    } catch (error) {
      // localStorage can be unavailable in private/crawler contexts; the visual state still applies.
    }
  };

  const initCollectionViewToggle = () => {
    if (!isCollectionOrSearchContext() || window.__pawuniCollectionViewToggleInitialized) {
      setCollectionView(getSavedCollectionView());
      return;
    }

    window.__pawuniCollectionViewToggleInitialized = true;
    setCollectionView(getSavedCollectionView());

    document.addEventListener('click', (event) => {
      const button = event.target.closest('.pawuni-collection-view-toggle__button[data-pawuni-collection-view]');
      if (!button) return;
      const view = button.dataset.pawuniCollectionView;
      if (!COLLECTION_VIEW_VALUES.has(view)) return;

      event.preventDefault();
      setCollectionView(view, { persist: true });
    });

    if (!window.MutationObserver || !document.body) return;

    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, '[data-pawuni-collection-view], .product-list--collection')) return;
      setCollectionView(getSavedCollectionView());
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const syncCollectionCopyToggle = (toggle) => {
    if (!(toggle instanceof HTMLElement)) return;

    const content = toggle.querySelector('[data-pawuni-collection-copy-content]');
    const control = toggle.querySelector('[data-pawuni-collection-copy-toggle-button]');
    if (!(content instanceof HTMLElement) || !(control instanceof HTMLButtonElement)) return;
    if (content.getClientRects().length === 0) return;

    const wasExpanded = toggle.classList.contains('is-expanded');
    toggle.classList.remove('is-expanded', 'is-clampable');

    const contentStyle = window.getComputedStyle(content);
    const lineHeight = parseFloat(contentStyle.lineHeight) || parseFloat(contentStyle.fontSize) * 1.6 || 24;
    const collapsedHeight = lineHeight * 3;
    const isClampable = content.scrollHeight > collapsedHeight + 4;

    toggle.classList.toggle('is-clampable', isClampable);
    control.hidden = !isClampable;

    if (isClampable && wasExpanded) {
      toggle.classList.add('is-expanded');
    }

    control.setAttribute('aria-expanded', toggle.classList.contains('is-expanded') ? 'true' : 'false');
  };

  const initCollectionCopyToggle = () => {
    if (!isCollectionOrSearchContext()) return;

    const syncAll = () => {
      document.querySelectorAll('[data-pawuni-collection-copy-toggle]').forEach(syncCollectionCopyToggle);
    };
    const syncAllThrottled = createRafThrottled(syncAll);

    syncAll();
    runWithDelays(syncAll, [180, 600, 1500]);

    if (window.__pawuniCollectionCopyToggleBound) return;
    window.__pawuniCollectionCopyToggleBound = true;

    document.addEventListener('click', (event) => {
      const control =
        event.target instanceof Element ? event.target.closest('[data-pawuni-collection-copy-toggle-button]') : null;
      if (!(control instanceof HTMLButtonElement)) return;

      const toggle = control.closest('[data-pawuni-collection-copy-toggle]');
      if (!(toggle instanceof HTMLElement)) return;

      toggle.classList.toggle('is-expanded');
      control.setAttribute('aria-expanded', toggle.classList.contains('is-expanded') ? 'true' : 'false');
    });

    window.addEventListener('resize', syncAllThrottled);

    if (!window.MutationObserver || !document.body) return;
    const observer = new MutationObserver((mutations) => {
      if (!mutationsContainAddedNode(mutations, '[data-pawuni-collection-copy-toggle]')) return;
      syncAllThrottled();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  };

  const initProductCardReviewCounts = () => {
    if (!isCollectionOrSearchContext() && !hasProductCardReviewContext()) return;
    if (typeof applySyntheticJudgeMePreviewCounts !== 'function') return;

    const run = () => applySyntheticJudgeMePreviewCounts(document);
    if (window.__pawuniCollectionProductCardReviewCountsInitialized) {
      run();
      return;
    }
    window.__pawuniCollectionProductCardReviewCountsInitialized = true;

    const rerunThrottled = createRafThrottled(run);

    run();
    runWithDelays(run, [600, 1800, 3600]);

    if (!window.MutationObserver || !document.body) return;

    const observer = new MutationObserver((mutations) => {
      if (
        !mutationsContainAddedNode(
          mutations,
          `.product-list, .slider-gallery, .rv-main, ${JDGM_PREVIEW_BADGE_SELECTOR}`,
        )
      ) {
        return;
      }
      rerunThrottled();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    bindJudgeMeAndTeeinblueEvents(rerunThrottled);
  };

  const initCollectionHeroShuffle = () => {
    const pool = document.querySelector('.pawuni-collection-hero__media[data-hero-pool]');
    if (!pool || pool.classList.contains('is-shuffled')) return;
    const tiles = Array.from(pool.querySelectorAll('[data-hero-tile]'));
    if (tiles.length === 0) return;
    for (let i = tiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    const chosen = tiles.slice(0, 3);
    tiles.slice(3).forEach((t) => t.remove());
    chosen.forEach((t, i) => {
      t.classList.add(`pawuni-collection-hero__tile--${i + 1}`);
      pool.appendChild(t);
    });
    pool.classList.add('is-shuffled');
  };

  const runCollectionHotfixes = () => {
    clearGlobalInteractionLocks();
    syncFacetedSortInputs();
    initCollectionViewToggle();
    initCollectionCopyToggle();
    initCollectionHeroShuffle();
    initProductCardReviewCounts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runCollectionHotfixes);
  } else {
    runCollectionHotfixes();
  }
})();
